"use client";

import { useEffect, useRef } from "react";

interface FooterRevealProps {
  children: React.ReactNode;
}

const PARALLAX_OFFSET = 0.4;

export function FooterReveal({ children }: FooterRevealProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const spacer = spacerRef.current;
    if (!wrapper || !spacer) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let raf = 0;
    const update = () => {
      raf = 0;
      const footerH = wrapper.offsetHeight;
      const viewH = window.innerHeight;
      const fits = footerH < viewH;

      if (!fits) {
        spacer.style.height = "0px";
        wrapper.style.position = "static";
        wrapper.style.transform = "";
        return;
      }

      spacer.style.height = `${footerH}px`;
      wrapper.style.position = "fixed";

      if (reduceMotion) {
        wrapper.style.transform = "translate3d(0, 0, 0)";
        return;
      }

      const docH = document.documentElement.scrollHeight;
      const scrollY = window.scrollY;
      const start = docH - viewH - footerH;
      const end = docH - viewH;
      const span = end - start;
      const progress =
        span <= 0 ? 1 : Math.max(0, Math.min(1, (scrollY - start) / span));
      const translate = (1 - progress) * PARALLAX_OFFSET * footerH;
      wrapper.style.transform = `translate3d(0, ${translate}px, 0)`;
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();
    const ro = new ResizeObserver(onScroll);
    ro.observe(wrapper);
    // Also watch the page body: streamed-in blocks, image loads and
    // client-side filtering change the document height without a scroll or
    // footer resize, which otherwise leaves the reveal math stale.
    ro.observe(document.body);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div ref={spacerRef} aria-hidden />
      {/* Static (in-flow) before hydration so the footer can never flash
          through streamed-in content; update() upgrades it to the fixed
          reveal once it has measured. bottom/left/right only apply then. */}
      <div
        ref={wrapperRef}
        className="bottom-0 left-0 right-0 z-0 will-change-transform shadow-[0_-24px_48px_-32px_rgba(0,0,0,0.35)]"
      >
        {children}
      </div>
    </>
  );
}
